import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Survey } from '../../../common/model/dto/survey/survey';
import OvSurveyQuestion from '../molecules/OvSurveyQuestion';
import OvCompactButton from '../atoms/OvCompactButton';
import { useTranslation } from 'react-i18next';
import { SurveyQuestionResponse } from '../../../common/model/dto/survey/survey-question-response';
import OvSurveySubmitScreen from '../atoms/OvSurveySubmitScreen';
import { SurveyService } from '../../../services/survey-service';
import OvErrorMessage from '../atoms/OvErrorMessage';
import CircularProgress from '@mui/material/CircularProgress';
import Colours from '../../../design-system/colours';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OvConfirmationDialog from '../molecules/OvConfirmationDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { PlatformTypeEnum } from '../../../common/model/type/platform-type.enum';
import { CreateSurveyResponse } from '../../../common/model/dto/survey/create-survey-response';

export interface OvSurveyProps {
  survey: Survey;
  userId?: string;
  instance_id?: string;
  isPreviewMode?: boolean;
  onSubmitSuccess?: () => void;
  onClose?: () => void;
}

const OvSurvey: FC<OvSurveyProps> = ({
  survey,
  userId,
  instance_id,
  isPreviewMode,
  onSubmitSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [surveyResponse, setSurveyResponse] = useState<CreateSurveyResponse>();
  let surveyElement;

  useEffect(() => {
    setSurveyResponse({
      survey_instance_id: instance_id,
      question_responses: survey.questions?.map(({ id, value }) => ({
        question_id: id,
        value,
        option_id: '',
      })),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey]);

  const handleResponseChange = (questionResponse: SurveyQuestionResponse) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      survey_instance_id: prevSurveyResponse!.survey_instance_id,
      question_responses:
        prevSurveyResponse?.question_responses?.map((response) =>
          response.question_id === questionResponse.question_id
            ? questionResponse
            : response
        ) || [],
    }));
  };

  const handleSubmit = async () => {
    if (isPreviewMode) {
      submitSuccess();
    } else {
      if (surveyResponse) {
        try {
          setIsLoading(true);

          const response = await SurveyService.sendSurveyResponse(
            mapSurveyResponse()
          );

          if (response) {
            submitSuccess();
          }
        } catch (error) {
          setErrorMessage((error as any)?.response?.data?.message);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const submitSuccess = () => {
    setIsSubmitted(true);

    if (onSubmitSuccess) {
      onSubmitSuccess();
    }
  };

  const handleClose = () => {
    if (isSubmitted && onClose) {
      onClose();
    } else {
      setIsOpenConfirmationDialog(true);
    }
  };

  const handleHiding = () => {
    setIsOpenConfirmationDialog(false);
    if (onClose) {
      onClose();
    }
  };

  const handleSnoozing = () => {
    if (userId && !isPreviewMode) {
      SurveyService.snoozeNpsSurvey(userId);
    }

    handleHiding();
  };

  const isCurrentStepValid = (): boolean => {
    const currentQuestionResponse = surveyResponse?.question_responses?.find(
      ({ question_id }) =>
        survey.questions && question_id === survey.questions[currentIndex].id
    );

    return (
      (survey.questions && !survey.questions[currentIndex].required) ||
      !!currentQuestionResponse?.value
    );
  };

  const mapSurveyResponse = (): CreateSurveyResponse => {
    const npsScoreRepresentQuestion = survey.questions?.find(
      ({ represent_nps_score }) => !!represent_nps_score
    );

    return {
      ...surveyResponse,
      question_responses: surveyResponse?.question_responses?.map((qr) => ({
        ...qr,
        represent_nps_score:
          qr.question_id && qr.question_id === npsScoreRepresentQuestion?.id
            ? true
            : undefined,
      })),
      platform: PlatformTypeEnum.CLINIC_DASHBOARD,
    };
  };

  if (survey.questions?.length) {
    surveyElement = (
      <>
        {!isSubmitted && (
          <>
            <OvSurveyQuestion
              value={
                surveyResponse?.question_responses &&
                surveyResponse?.question_responses[currentIndex].value
              }
              onResponseChange={handleResponseChange}
              question={survey.questions[currentIndex]}
              key={survey.questions[currentIndex].id}
            />

            <ActionContainer>
              {currentIndex !== 0 && (
                <StyledOvCompactButton
                  onClick={() => setCurrentIndex(currentIndex - 1)}
                >
                  {t('common.actions.prev')}
                </StyledOvCompactButton>
              )}
              {currentIndex !== survey.questions?.length - 1 ? (
                <StyledOvCompactButton
                  disabled={!isCurrentStepValid()}
                  onClick={() => setCurrentIndex(currentIndex + 1)}
                >
                  {t('common.actions.next')}
                </StyledOvCompactButton>
              ) : (
                <StyledOvCompactButton
                  disabled={!isCurrentStepValid()}
                  onClick={handleSubmit}
                >
                  {t('common.submit')}

                  {isLoading && <StyledSpinner />}
                </StyledOvCompactButton>
              )}
            </ActionContainer>

            {errorMessage && (
              <ErrorMessageContainer>
                <OvErrorMessage message={errorMessage} />
              </ErrorMessageContainer>
            )}
          </>
        )}

        {isSubmitted && survey.submit_screen && (
          <OvSurveySubmitScreen
            title={survey.submit_screen.title}
            description={survey.submit_screen.description || ''}
          />
        )}

        <OvConfirmationDialog
          icon={<StyledWarningIcon />}
          isOpen={isOpenConfirmationDialog}
          onCancel={handleHiding}
          onConfirm={handleSnoozing}
          description="survey.snoozingDesc"
          title="common.areYouSure"
          cancelLabel={t('common.actions.hide')}
          confirmLabel={t('common.actions.snooze')}
        />
      </>
    );
  }

  return (
    <>
      {surveyElement}

      <StyledCloseIconButton onClick={handleClose}>
        <CloseIcon />
      </StyledCloseIconButton>
    </>
  );
};

export default OvSurvey;

const ActionContainer = styled.div`
  margin-top: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const StyledOvCompactButton = styled(OvCompactButton)`
  && {
    padding: 0.25rem 2rem;
    width: 7rem;
  }
`;

const StyledSpinner = styled(CircularProgress)`
  && {
    color: ${Colours.WHITE};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem !important;
    height: 1rem !important;
    margin-left: 0.5rem;

    .MuiCircularProgress-svg {
      width: 1rem;
    }
  }
`;

const ErrorMessageContainer = styled.div`
  margin: 0.5rem auto;
  display: flex;
  justify-content: center;
`;

const StyledCloseIconButton = styled(IconButton)`
  && {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    padding: 0;
    margin: 0;
    vertical-align: bottom;

    svg {
      height: 1rem;
      width: 1rem;
      color: #6c6e70;
    }

    &:hover {
      background: none;
    }

    .MuiTouchRipple-root {
      display: none !important;
    }
  }
`;

const StyledWarningIcon = styled(WarningIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
